/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import error from '../../../../common/nodejs/errors';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  WAIT: "wait",
  START: "start",
  SUCCESS: "success",
  DISPLAY: "display",
  SETTINGFAILED: "settingfailed",
  DISPLAYFAILED: "displayfailed",
  ERROR: "error"
};

/** API type定義 */
const API_TYPE = {
  START: 'start',
  TERMINATE: 'terminate',
  EXECUTE: 'execute',
  STOP: 'stop'
};

/** eCall ON/OFF設定文字列定義 */
const E_CALL_SET_VALUE_STRING = {
  ON: 'ON',
  OFF: 'OFF',
};

export default {
  name: "requirement081",
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      subscriptionStatusMessage: null,
      i18nWsDidKey: null,
      modal_requirement_081: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.SETTINGFAILED:
        case PHASE.DISPLAYFAILED:
          return "danger";
        case PHASE.SUCCESS:
        case PHASE.DISPLAY:
          return "success";
        case PHASE.WAIT:
        case PHASE.START:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_081 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_081 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** Subscription Statusを取得する */
    async getSubscriptionStatus() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.START
        };

        // start API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              // 正常終了
              // 取得したデータを画面に表示。文言の引き当てが存在しない場合、data_recordの16進数を表示する
              // subscription_statusの一番最後のアンダーバー(_)より後ろの桁
              const status = response.data.user_data.subscription_status;
              const subscriptionStatusString = this.$te(`${this.i18nWsDidKey}.` + status) ? 
                this.$t(`${this.i18nWsDidKey}.` + status) : `0x${status.slice(status.lastIndexOf('_') + 1).toUpperCase()}`;
              this.subscriptionStatusMessage = this.$t(`${this.i18nWsKey}.REQ_WS_81_TXT_1`).replace('$DID$', subscriptionStatusString);
              this.phase = PHASE.START;
              break;
            }
            case config.STATUS_WSP_FAILED:
              this.showUnexpectedError();
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 「ON」「OFF」押下
     * @param {string}  setValue 「ON」或いは「OFF」の設定内容
     */
    async changeSetting(setValue) {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.EXECUTE,
          eCallsetValue: setValue
        };

        // execute API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_081_EXECUTE_FAILED:
                  // 独自エラー
                  this.phase = PHASE.SETTINGFAILED;
                  break;
                default:
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** サブスクリプションステータス取得結果表示 */
    async showSubscriptionStatus() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.STOP
        };

        // stop API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              // 正常終了
              // 取得したデータを画面に表示。文言の引き当てが存在しない場合、data_recordの16進数を表示する
              // subscription_statusの一番最後のアンダーバー(_)より後ろの桁
              const status = response.data.user_data.subscription_status;
              const subscriptionStatusString = this.$te(`${this.i18nWsDidKey}.` + status) ? 
                this.$t(`${this.i18nWsDidKey}.` + status) : `0x${status.slice(status.lastIndexOf('_') + 1).toUpperCase()}`;
              this.subscriptionStatusMessage = this.$t(`${this.i18nWsKey}.REQ_WS_81_TXT_3`).replace('$DID$', subscriptionStatusString);
              this.phase = PHASE.DISPLAY;
              break;
            }
            case config.STATUS_WSP_FAILED:
              // 取得失敗表示
              this.phase = PHASE.DISPLAYFAILED;
              break;
            default:
              // 異常終了
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async doTerminate() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE
        };

        // terminate API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
              if (this.phase === PHASE.ERROR) {
                // 共通エラー画面の場合は、ウィザードを閉じる
                this.closeWizard();
              } else {
                // 共通エラー
                this.showUnexpectedError();
                break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ユーザ操作
    /** 初期画面 ONボタン押下 */
    onStartOnClicked() {
      this.changeSetting(E_CALL_SET_VALUE_STRING.ON);
    },

    /** 初期画面 OFFボタン押下 */
    onStartOffClicked() {
      this.changeSetting(E_CALL_SET_VALUE_STRING.OFF);
    },

    /** 初期画面 キャンセルボタン押下 */
    onStartCancelClicked() {
      this.closeWizard();
    },

    /** 正常終了画面 OKボタン押下 */
    onSuccessOkClicked() {
      this.showSubscriptionStatus();
    },

    /** 結果表示画面 OKボタン押下 */
    onDisplayOkClicked() {
      this.doTerminate();
    },

    /** 設定失敗表示画面 OKボタン押下 */
    onSettingFailedOkClicked() {
      this.doTerminate();
    },

    /** 取得失敗表示画面 OKボタン押下 */
    onDisplayFailedOkClicked() {
      this.doTerminate();
    },

    /** 共通エラー画面 OKボタン押下 */
    onErrorOkClicked() {
      this.doTerminate();
    }
  },

  async mounted() {
    this.init();
    const systemId = individualDiagnose.getCurrentSystemId(this);
    this.i18nWsDidKey = `wsDid${systemId}`;

    await this.getSubscriptionStatus();
  }
};
